import React, { forwardRef, useState, type ReactNode, type ButtonHTMLAttributes } from 'react';

/* ─── helpers ─── */
function cn(...args: Array<string | undefined | false | null>) {
    return args.filter(Boolean).join(' ');
}

/* ─── types ─── */
export type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger' | 'link';
export type ButtonSize = 'small' | 'medium' | 'large';

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'style'> {
    variant?: ButtonVariant;
    size?: ButtonSize;
    loading?: boolean;
    leftIcon?: ReactNode;
    rightIcon?: ReactNode;
    fullWidth?: boolean;
    children?: ReactNode;
    className?: string;
    style?: React.CSSProperties;
}

/* ─── size classes ─── */
const sizeClasses: Record<ButtonSize, string> = {
    small: 'text-[13px] px-3.5 py-[7px] min-h-[34px]',
    medium: 'text-sm px-[18px] py-[9px] min-h-[38px]',
    large: 'text-[15px] px-6 py-[11px] min-h-[44px]',
};

/* ─── variant classes (v2-token based) ─── */
const variantClasses: Record<ButtonVariant, string> = {
    primary:
        'text-white border border-transparent ' +
        'shadow-[0_2px_6px_rgba(4,94,184,0.22),inset_0_1px_0_rgba(255,255,255,0.10)] ' +
        'hover:shadow-[0_4px_12px_rgba(4,94,184,0.30),inset_0_1px_0_rgba(255,255,255,0.12)]',

    secondary:
        'bg-[var(--v2-bg-glass-strong)] text-[var(--v2-text-primary)] border border-[var(--v2-border-glass)] ' +
        'hover:bg-[var(--v2-bg-hover)] hover:border-[var(--v2-border-glass-strong)]',

    outline:
        'bg-transparent text-[var(--v2-text-secondary)] border border-[var(--v2-border-interactive)] ' +
        'hover:bg-[var(--v2-bg-hover)] hover:border-[var(--v2-primary-400)] hover:text-[var(--v2-text-primary)]',

    ghost:
        'bg-transparent text-[var(--v2-text-secondary)] border border-transparent ' +
        'hover:bg-[var(--v2-bg-hover)] hover:text-[var(--v2-text-primary)]',

    danger:
        'text-white border border-transparent ' +
        'shadow-[0_2px_6px_rgba(204,78,78,0.22),inset_0_1px_0_rgba(255,255,255,0.10)] ' +
        'hover:shadow-[0_4px_12px_rgba(204,78,78,0.30),inset_0_1px_0_rgba(255,255,255,0.12)]',

    link:
        'bg-transparent text-[var(--v2-primary-500)] border-none underline-offset-2 ' +
        'hover:underline hover:text-[var(--v2-primary-400)] p-0 min-h-0',
};

/* ─── gradient styles (can't express in Tailwind) ─── */
function getGradientStyle(variant: ButtonVariant): React.CSSProperties | undefined {
    if (variant === 'primary') {
        return { background: 'linear-gradient(to bottom, var(--v2-primary-500), var(--v2-primary-600))' };
    }
    if (variant === 'danger') {
        return { background: 'linear-gradient(to bottom, var(--v2-danger), var(--v2-danger-muted))' };
    }
    return undefined;
}

/* ─── spinner keyframes (injected once) ─── */
const spinnerCSS = `@keyframes v2-btn-spin{to{transform:rotate(360deg)}}`;
let injected = false;
function ensureSpinnerCSS() {
    if (injected || typeof document === 'undefined') return;
    const s = document.createElement('style');
    s.textContent = spinnerCSS;
    document.head.appendChild(s);
    injected = true;
}

/* ─── per-variant spinner colors ─── */
function getSpinnerStyle(variant: ButtonVariant): React.CSSProperties {
    // Filled variants (white text) → white spinner on semi-transparent white track
    if (variant === 'primary' || variant === 'danger') {
        return {
            borderColor: 'rgba(255,255,255,0.3)',
            borderTopColor: '#fff',
            filter: 'drop-shadow(0 0 2px rgba(255,255,255,0.4))',
        };
    }
    // Non-filled variants → use primary-500 arc on a subtle track for both modes
    return {
        borderColor: 'var(--v2-border-glass, rgba(128,128,128,0.25))',
        borderTopColor: 'var(--v2-primary-500, #045eb8)',
        filter: 'drop-shadow(0 0 1px var(--v2-primary-500, #045eb8))',
    };
}

/* ─── component ─── */
const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
    {
        variant = 'primary',
        size = 'medium',
        loading = false,
        leftIcon,
        rightIcon,
        fullWidth = false,
        disabled,
        children,
        className = '',
        style,
        type = 'button',
        ...rest
    },
    ref
) {
    const isDisabled = disabled || loading;
    const [isFocused, setIsFocused] = useState(false);

    if (loading) ensureSpinnerCSS();

    const focusRingStyle: React.CSSProperties = isFocused
        ? { boxShadow: '0 0 0 3px var(--v2-focus-ring)' }
        : {};

    return (
        <button
            ref={ref}
            type={type}
            disabled={isDisabled}
            className={cn(
                // Base
                'inline-flex items-center justify-center gap-2 leading-none whitespace-nowrap relative overflow-hidden',
                'rounded-[var(--v2-radius-md)] font-semibold tracking-[-0.012em]',
                'transition-all duration-[var(--v2-duration-fast)] ease-[var(--v2-ease-calm)]',
                'focus:outline-none',
                // Loading → keep full opacity + wait cursor; Disabled → gently dim + not-allowed
                loading ? 'cursor-wait' : disabled ? 'opacity-65 cursor-not-allowed' : '',
                // Size
                variant !== 'link' ? sizeClasses[size] : sizeClasses[size],
                // Width
                fullWidth ? 'w-full' : '',
                // Variant
                variantClasses[variant],
                className,
            )}
            style={{
                ...getGradientStyle(variant),
                ...focusRingStyle,
                ...style,
            }}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            {...rest}
        >
            {/* Loading spinner */}
            {loading && (
                <span
                    className="inline-block w-[18px] h-[18px] rounded-full flex-shrink-0"
                    style={{
                        animation: 'v2-btn-spin 0.6s linear infinite',
                        borderWidth: '2.5px',
                        borderStyle: 'solid',
                        ...getSpinnerStyle(variant),
                    }}
                    aria-hidden="true"
                />
            )}

            {/* Left icon */}
            {!loading && leftIcon && (
                <span className="inline-flex items-center justify-center w-[18px] h-[18px] flex-shrink-0">
                    {leftIcon}
                </span>
            )}

            {/* Label */}
            {children && <span>{children}</span>}

            {/* Right icon */}
            {rightIcon && (
                <span className="inline-flex items-center justify-center w-[18px] h-[18px] flex-shrink-0">
                    {rightIcon}
                </span>
            )}
        </button>
    );
});

export default Button;
